import type { NextRouter } from "next/router"
import type { ReactNode } from "react"
import React from "react"

import type { Geolocation } from "@/lib/geolocation"
import { extractLatlngFromBrowserState } from "@/lib/geolocation/browser/extract_latlng"
import { fetchBrowserGeolocationState } from "@/lib/geolocation/browser/fetch_state"
import { geolocationNotices } from "@/lib/geolocation/container/notices"
import { GeolocationContext } from "@/lib/geolocation/context"
import { defaultDistanceOption } from "@/lib/geolocation/distance/default"
import {
  distanceKeyToOption,
  distanceOptionToKey,
} from "@/lib/geolocation/distance/distance"
import { isDistanceKey } from "@/lib/geolocation/distance/isKey"
import { parseDistance } from "@/lib/geolocation/distance/parse"
import { isGeolocationPosition } from "@/lib/geolocation/is_position"
import { fetchGeolocationAutocompleteFromLocationIQ } from "@/lib/geolocation/locationiq/fetch_autocomplete"
import { fetchReverseGeocodeFromLocationIQ } from "@/lib/geolocation/locationiq/fetch_reverse_geocode"
import { toPosition } from "@/lib/geolocation/to_position"
import { Loading } from "@/lib/loading"
import type { Notices } from "@/lib/notices"
import { ProcessLocking } from "@/lib/process_locking"
import { ReactPerformance } from "@/lib/react_performance"
import { updateQueryParams } from "@/lib/routing/updateQueryParams"

type Props = {
  children: ReactNode
  loading: Loading.ContextData
  noticeFunctions: Notices.ContextFunctions
  router: NextRouter
}

type State = {
  address?: Geolocation.Bing.Address
  distanceOption: Geolocation.DistanceOption
  fetchingSuggestions: boolean
  latlng?: Geolocation.Latlng
  loadingConfig: Loading.Config
  permissionDenied: boolean
  position?: Geolocation.Position
  processing: string | void
  suggestions: Geolocation.Suggestion[]
  suggestionCoordinates: Geolocation.Latlng[]
  update: boolean
}

export class GeolocationContainerComponent extends React.Component<
  Props,
  State
> {
  public state: State = {
    address: undefined,
    distanceOption: defaultDistanceOption,
    fetchingSuggestions: false,
    latlng: undefined,
    loadingConfig: {
      dependencies: [`notices`, `routing`],
      processId: `geolocation`,
    },
    permissionDenied: false,
    processing: undefined,
    position: undefined,
    suggestions: [],
    suggestionCoordinates: [],
    update: false,
  }

  private chooseSuggestion: Geolocation.Functions.ChooseSuggestion = async ({
    suggestion,
  }) => {
    // Find the index of the suggestion to get corresponding coordinates
    const index = this.state.suggestions.findIndex((s) => {
      return s.formattedAddress === suggestion.formattedAddress
    })

    if (index !== -1 && this.state.suggestionCoordinates[index]) {
      const latlng = this.state.suggestionCoordinates[index]
      const position = toPosition(latlng)

      return this.updateAddress({
        address: suggestion,
        latlng,
        position,
      })
    }

    return this.updateAddress({ address: suggestion })
  }

  public componentDidMount = () => {
    this.onMaybeReady()
  }

  public componentDidUpdate = () => {
    this.onMaybeReady()
  }

  private onMaybeReady = Loading.curryOnMaybeReady(this, {
    onDependenciesResolved: async () => {
      await this.fetchGeolocationFromRouterParams()
    },
  })

  private fetchGeolocationFromRouterParams = async () => {
    const { distance, position, search } = this.props.router.query

    const otherUpdates = isDistanceKey(distance)
      ? { distanceOption: distanceKeyToOption(distance) }
      : {}

    if (isGeolocationPosition(position)) {
      const [lat, lng] = position.split(`,`).map(parseFloat)
      const latlng = { lat, lng }

      const address = await fetchReverseGeocodeFromLocationIQ({
        lat,
        lon: lng,
      })

      if (address) {
        await this.updateAddress({
          address,
          latlng,
          otherUpdates,
          position,
        })
        return
      }
    }

    if (!search) {
      if (Object.keys(otherUpdates).length > 0) {
        await this.updateState(otherUpdates)
      }
      return
    }

    const query = search.toString()

    const { suggestions, coordinates } =
      await fetchGeolocationAutocompleteFromLocationIQ({
        query,
        limit: 1,
      })

    if (suggestions.length === 0) {
      return
    }

    const address = suggestions[0]
    const latlng = coordinates[0]
    const positionStr = latlng ? toPosition(latlng) : undefined

    await this.updateAddress({
      address,
      latlng,
      otherUpdates,
      position: positionStr,
    })
  }

  private fetchSuggestions: Geolocation.Functions.FetchSuggestions = async ({
    entityTypes,
    input,
  }) => {
    if (this.state.fetchingSuggestions || input === ``) {
      return false
    }

    this.setState({ fetchingSuggestions: true })

    const { suggestions, coordinates } =
      await fetchGeolocationAutocompleteFromLocationIQ({
        query: input,
        limit: 10,
      })

    this.updateState({
      fetchingSuggestions: false,
      suggestions,
      suggestionCoordinates: coordinates,
    })

    return true
  }

  private useMyLocation: Geolocation.Functions.UseMyLocation = async () => {
    return ProcessLocking.asLockedProcess({
      component: this,
      name: `GeolocationContainer#useMyLocation`,
      process: async () => {
        this.removeNotices()

        const browserState = await fetchBrowserGeolocationState()

        if (browserState.position) {
          const latlng = extractLatlngFromBrowserState({ browserState })
          const position = toPosition(latlng)

          const address = await fetchReverseGeocodeFromLocationIQ({
            lat: latlng.lat,
            lon: latlng.lng,
          })

          if (address) {
            this.updateState({
              address,
              latlng,
              permissionDenied: false,
              position,
            })

            return { address, position }
          }

          // Handle case where address is null/undefined
          return { address: undefined, position: undefined }
        }

        this.displayNotice({ browserState })

        if (browserState.permission.state === `denied`) {
          this.updateState({
            permissionDenied: true,
          })
        }

        return {
          address: undefined,
          position: undefined,
        }
      },
    })
  }

  public render = (): ReactNode => {
    return (
      <GeolocationContext.Provider
        value={{
          address: this.state.address,
          chooseDistance: this.chooseDistance,
          chooseSuggestion: this.chooseSuggestion,
          distance: this.state.distanceOption,
          distanceKey: distanceOptionToKey(this.state.distanceOption),
          fetchSuggestions: this.fetchSuggestions,
          formattedAddress: this.state.address?.formattedAddress,
          latlng: this.state.latlng,
          permissionDenied: this.state.permissionDenied,
          position: this.state.position,
          processing: this.state.processing,
          reset: this.reset,
          suggestions: this.state.suggestions,
          useMyLocation: this.useMyLocation,
        }}
      >
        {this.props.children}
      </GeolocationContext.Provider>
    )
  }

  private reset: Geolocation.Functions.Reset = () => {
    this.updateState({
      address: undefined,
      distanceOption: defaultDistanceOption,
      latlng: undefined,
      position: undefined,
      suggestions: [],
      suggestionCoordinates: [],
    })
  }

  private chooseDistance: Geolocation.Functions.ChooseDistance = async (
    distanceKeyOrOption
  ) => {
    const { distanceKey, distanceOption } = parseDistance(distanceKeyOrOption)

    if (distanceOption === this.state.distanceOption) {
      return
    }

    await this.updateState({ distanceOption }, () => {
      return updateQueryParams({
        params: { distance: distanceKey },
        router: this.props.router,
      })
    })
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    Props,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps, nextState }) => {
      if (Loading.shouldComponentReady(nextProps, nextState)) {
        return { update: true }
      }

      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  private updateAddress = async ({
    address,
    latlng,
    otherUpdates,
    position,
  }: {
    address: Geolocation.Bing.Address
    latlng?: Geolocation.Latlng
    otherUpdates?: ReactPerformance.Update<State>
    position?: Geolocation.Position
  }): Promise<{
    address: Geolocation.Bing.Address
    position: Geolocation.Position | void
  }> => {
    this.removeNotices()

    if (latlng && position) {
      this.updateState({
        ...otherUpdates,
        address,
        latlng,
        position,
      })

      return { address, position }
    }

    // If we don't have coordinates, we can't determine position
    // This shouldn't happen with LocationIQ as coordinates come with suggestions
    console.error(
      `LocationIQ integration: Address without coordinates detected. This is unexpected.`
    )
    this.updateState({
      ...otherUpdates,
      address,
    })

    return { address, position: undefined }
  }

  private displayNotice = ({
    browserState,
  }: {
    browserState: Geolocation.BrowserState
  }) => {
    const { PermissionDenied, TimedOut, Unavailable } =
      geolocationNotices.UseMyLocation

    if (browserState.timedOut) {
      this.props.noticeFunctions.displayNotice(TimedOut)
    }

    const { positionError } = browserState

    if (positionError) {
      const { code } = positionError
      if (code === positionError.TIMEOUT) {
        this.props.noticeFunctions.displayNotice(TimedOut)
      }

      if (code === positionError.PERMISSION_DENIED) {
        this.props.noticeFunctions.displayNotice(PermissionDenied)
      }

      if (code === positionError.POSITION_UNAVAILABLE) {
        this.props.noticeFunctions.displayNotice(Unavailable)
      }
    }
  }

  private removeNotices = () => {
    Object.values(geolocationNotices.UseMyLocation).forEach((notice) => {
      this.props.noticeFunctions.removeNotice(notice)
    })
  }

  private updateState = ReactPerformance.curryUpdateManagedState(this)
}
